import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import {
  Dropdown,
  EnumDropdownItemVariant,
  type IDropdownItem,
  Tooltip,
  Typography,
} from 'shared/ui'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { type CustomField, FieldTypeConfig } from 'entities/CustomField'
import { type DateFieldDropdownStore } from './store/DateFieldDropdownStore'
import styles from './styles.module.scss'

type IDateFieldDropdown = {
  dropdownId: string
  store: DateFieldDropdownStore
  getDropdownItemProps?: (field: CustomField) => Partial<IDropdownItem>
}

export const DateFieldDropdown = observer(
  ({ dropdownId, store, getDropdownItemProps }: IDateFieldDropdown) => {
    const { dateFiled, setDateFiled, customFields, init, loading } = store

    useEffect(() => {
      init()
    }, [])

    const onChange = (item: IDropdownItem) => {
      setDateFiled(item.data)
    }

    const items: IDropdownItem[] = customFields.map((field) => ({
      id: field.key,
      label: field.name,
      data: field,
      iconL: FieldTypeConfig[field.type].icon,
      activeValue: field.key === dateFiled?.key,
      variant: EnumDropdownItemVariant.CheckedRight,
      ...getDropdownItemProps?.(field),
    }))

    return (
      <Dropdown
        width={330}
        withSearch
        parentsDropdownId={[dropdownId]}
        items={items}
        onChange={onChange}
        margin={0}
        propsDropdownList={{
          labelItemsContent: (
            <Typography
              ariaLabel={'custom_fields'}
              variant={'body-small-medium'}
              color={'tertiary'}
              className={styles.labelDropdown}
              tag={'div'}
            >
              Custom fields
            </Typography>
          ),
          loading: !items.length && loading,
          maxHeight: 232,
          textFieldProps: {
            InputProps: {
              placeholder: 'Search',
            },
          },
          notFoundText: 'No date / date & time fields',
        }}
        triggerComponent={() => (
          <Tooltip label={'Field'} placement={'left'} fullWidth>
            <div>
              <TextFieldPlaceholder
                effect={'collapse'}
                variant={'outline'}
                size={'small'}
                leftActions={[
                  makeTextFieldIcon({
                    icon: (dateFiled && FieldTypeConfig[dateFiled.type].icon) || 'calendar',
                    className: styles.icon,
                  }),
                ]}
                rightActions={[
                  makeTextFieldIcon({
                    icon: 'chevronDown',
                    color: 'var(--content-neutral-primary)',
                  }),
                ]}
                placeholder={
                  dateFiled ? (
                    <Typography variant={'text-input-field-r'} ariaLabel={'dateFiled'} ellipsis>
                      {dateFiled.name}
                    </Typography>
                  ) : (
                    <Typography
                      variant={'text-input-field-r'}
                      color={'var(--content-primary-disabled)'}
                      ariaLabel={'placeholder'}
                    >
                      Select field
                    </Typography>
                  )
                }
              />
            </div>
          </Tooltip>
        )}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                offset: ({ reference }) => {
                  return [0, -reference.height - 4]
                },
              },
            },
          ],
        }}
      />
    )
  }
)
